import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Copy, Download } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import {
  copyPreviousWeek,
  exportTimeCsv,
  getWeeklyGrid,
} from "@/api/time";
import type { WeeklyGridResponse } from "@/types/time";

function toMonday(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  const day = copy.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  return copy;
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface Props {
  employeeId?: string; // managers/HR can target a specific employee
  canExport?: boolean;
}

export function WeeklyTimesheetGrid({ employeeId, canExport = false }: Props) {
  const { addToast } = useToast();
  const [weekStart, setWeekStart] = useState<Date>(toMonday(new Date()));
  const [grid, setGrid] = useState<WeeklyGridResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isoWeek = useMemo(() => isoDate(weekStart), [weekStart]);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getWeeklyGrid(isoWeek, employeeId);
      setGrid(data);
    } catch {
      addToast("error", "Failed to load weekly grid");
      setGrid(null);
    } finally {
      setIsLoading(false);
    }
  }, [isoWeek, employeeId, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const shiftWeek = (deltaDays: number) => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + deltaDays);
    setWeekStart(toMonday(next));
  };

  const weekLabel = useMemo(() => {
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    const f = (d: Date) =>
      d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    return `${f(weekStart)} — ${f(end)}, ${end.getFullYear()}`;
  }, [weekStart]);

  const handleCopyPrevious = async () => {
    try {
      const r = await copyPreviousWeek(isoWeek);
      addToast("success", `Copied ${r.copied} entries from previous week`);
      await load();
    } catch {
      addToast("error", "Copy failed");
    }
  };

  const handleExport = async () => {
    try {
      const end = new Date(weekStart);
      end.setDate(end.getDate() + 6);
      const blob = await exportTimeCsv(
        isoWeek,
        isoDate(end),
        employeeId,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `timesheet-${isoWeek}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      addToast("error", "Export failed");
    }
  };

  return (
    <Card>
      <div className="flex items-center gap-3 mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => shiftWeek(-7)}
          title="Previous week"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <p className="text-sm font-semibold text-text-primary">
            Week of {weekLabel}
          </p>
          {grid && (
            <p className="text-xs text-text-muted mt-0.5">
              {grid.total_hours}h total · {grid.regular_hours}h reg
              {grid.overtime_hours > 0 && ` · ${grid.overtime_hours}h OT`}
              {grid.billable_hours > 0 && ` · ${grid.billable_hours}h billable`}
            </p>
          )}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCopyPrevious}
          title="Copy entries from previous week"
        >
          <Copy className="h-4 w-4" />
          Copy prev week
        </Button>
        {canExport && (
          <Button variant="secondary" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4" />
            CSV
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => shiftWeek(7)}
          title="Next week"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-sm text-text-muted">Loading…</div>
      ) : !grid || grid.rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-text-muted">
          No hours logged this week.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60 text-left">
                <th className="py-2 font-medium text-text-muted">Project</th>
                {DAY_LABELS.map((d, i) => {
                  const day = new Date(weekStart);
                  day.setDate(day.getDate() + i);
                  return (
                    <th
                      key={d}
                      className="py-2 text-center font-medium text-text-muted"
                    >
                      <div>{d}</div>
                      <div className="text-[10px] text-text-muted/70">
                        {day.getDate()}
                      </div>
                    </th>
                  );
                })}
                <th className="py-2 text-right font-medium text-text-muted">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((row, idx) => (
                <tr key={idx} className="border-b border-border/30">
                  <td className="py-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-text-muted">
                        {row.project_code ?? "—"}
                      </span>
                      <span className="text-text-primary">
                        {row.project_name || "(no project)"}
                      </span>
                      {row.is_billable && (
                        <Badge variant="info">$</Badge>
                      )}
                    </div>
                  </td>
                  {row.cells.map((c) => (
                    <td
                      key={c.date}
                      className="py-2 text-center tabular-nums text-text-primary"
                    >
                      {c.hours > 0 ? c.hours.toFixed(2) : "—"}
                    </td>
                  ))}
                  <td className="py-2 text-right font-semibold tabular-nums text-text-primary">
                    {row.total_hours.toFixed(2)}
                  </td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td className="py-3">Total</td>
                {DAY_LABELS.map((_, i) => {
                  const dayTotal = grid.rows.reduce(
                    (sum, r) => sum + r.cells[i].hours,
                    0,
                  );
                  return (
                    <td
                      key={i}
                      className="py-3 text-center tabular-nums text-text-primary"
                    >
                      {dayTotal > 0 ? dayTotal.toFixed(2) : "—"}
                    </td>
                  );
                })}
                <td className="py-3 text-right tabular-nums text-text-primary">
                  {grid.total_hours.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
